import React from 'react';
import { Form, Button, Modal } from '@douyinfe/semi-ui';
import ImageSelector from '../../../ImageSelector';
import LinkSelector from '../../../LinkSelector';
import { ModuleStyleForm } from '../../components/ModuleBase/ModuleBase';
import { config } from './config';

export const HotspotImageStyleForm = ({ data = {}, onChange }) => {
    const defaultData = {
        ...config.defaultProps
    };

    // 只在初始化时合并默认值，而不是每次更新时都合并
    React.useEffect(() => {
        // 确保使用默认值时，坐标和宽高是数字类型
        const initialData = { ...defaultData, ...data };

        // 无论是否有热区数据，都确保热区数组存在
        if (!initialData.hotspots) {
            initialData.hotspots = [];
        }

        // 确保所有热区的坐标和宽高都是数字类型
        if (initialData.hotspots.length > 0) {
            initialData.hotspots = initialData.hotspots.map(hotspot => ({
                ...hotspot,
                x: typeof hotspot.x === 'number' && !isNaN(hotspot.x) ? hotspot.x : 50,
                y: typeof hotspot.y === 'number' && !isNaN(hotspot.y) ? hotspot.y : 50,
                w: typeof hotspot.w === 'number' && !isNaN(hotspot.w) ? hotspot.w : 100,
                h: typeof hotspot.h === 'number' && !isNaN(hotspot.h) ? hotspot.h : 100
            }));
        }

        // 更新数据
        onChange?.(initialData);
    }, []);

    const handleImageChange = (value) => {
        console.log('图片变更:', value);
        onChange?.({ ...data, image: value });
    };

    const handleHotspotAdd = () => {
        // 确保hotspots是数组
        const hotspots = Array.isArray(data.hotspots) ? [...data.hotspots] : [];

        // 添加新热区，确保使用有效的数字
        hotspots.push({
            x: 50,  // 直接使用数字字面量，避免不必要的转换
            y: 50,
            w: 100,
            h: 100,
            l: ''
        });

        console.log('添加热区:', hotspots);
        onChange?.({ ...data, hotspots });
    };

    const handleHotspotChange = (index, field, value) => {
        console.log(`修改热区 ${index} 的 ${field} 为 ${value}`, typeof value);
        const hotspots = [...(data.hotspots || [])];
        // 确保值是数字类型
        if (field === 'x' || field === 'y' || field === 'w' || field === 'h') {
            // 转换为数字并处理NaN情况
            const numValue = Number(value);
            value = !isNaN(numValue) ? numValue : (field === 'x' || field === 'y' ? 50 : 100);
        }
        hotspots[index] = { ...hotspots[index], [field]: value };
        console.log('更新后的热区数据:', hotspots[index]);
        onChange?.({ ...data, hotspots });
    };

    const handleHotspotDelete = (index) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这个热区吗？',
            onOk: () => {
                const hotspots = [...(data.hotspots || [])];
                hotspots.splice(index, 1);
                console.log('删除热区后:', hotspots);
                onChange?.({ ...data, hotspots });
            }
        });
    };

    const handleStyleChange = (values) => {
        console.log('修改图片样式:', values);
        onChange?.({ ...data, imageStyle: { ...data.imageStyle, ...values } });
    };

    return (
        <Form labelPosition="left" labelWidth={100}>
            <Form.Section text="图片设置">
                <Form.Label>选择图片</Form.Label>
                <ImageSelector value={data.image || defaultData.image} onChange={handleImageChange} />
                <Form
                    initValues={{
                        height: data?.imageStyle?.height || defaultData.imageStyle.height,
                        borderRadius: data?.imageStyle?.borderRadius || defaultData.imageStyle.borderRadius
                    }}
                    onValueChange={handleStyleChange}
                >
                    <Form.InputNumber
                        field="height"
                        label="高度"
                    />
                    <Form.InputNumber
                        field="borderRadius"
                        label="圆角"
                    />
                </Form>
            </Form.Section>

            <Form.Section text="热区设置">
                <Button onClick={handleHotspotAdd}>添加热区</Button>
                {(data.hotspots || []).map((hotspot, index) => {
                    const defaultHotspot = { x: 50, y: 50, w: 100, h: 100, l: '' };
                    return (
                        <Form.Section key={index} text={`热区 ${index + 1}`}>
                            <Form
                                initValues={{
                                    x: typeof hotspot.x === 'number' && !isNaN(hotspot.x) ? hotspot.x : defaultHotspot.x,
                                    y: typeof hotspot.y === 'number' && !isNaN(hotspot.y) ? hotspot.y : defaultHotspot.y,
                                    w: typeof hotspot.w === 'number' && !isNaN(hotspot.w) ? hotspot.w : defaultHotspot.w,
                                    h: typeof hotspot.h === 'number' && !isNaN(hotspot.h) ? hotspot.h : defaultHotspot.h
                                }}
                                onValueChange={(values) => {
                                    // 当表单值变化时，更新对应的热区数据
                                    Object.keys(values).forEach(field => {
                                        if (field === 'x' || field === 'y' || field === 'w' || field === 'h') {
                                            handleHotspotChange(index, field, values[field]);
                                        }
                                    });
                                }}
                            >
                                <Form.InputNumber
                                    field="x"
                                    label="X坐标"
                                    help="基于图片宽度750rpx的比例"
                                    min={0}
                                    precision={0}
                                    style={{ width: '100%' }}
                                />
                                <Form.InputNumber
                                    field="y"
                                    label="Y坐标"
                                    help="基于图片宽度750rpx的比例"
                                    min={0}
                                    precision={0}
                                    style={{ width: '100%' }}
                                />
                                <Form.InputNumber
                                    field="w"
                                    label="宽度"
                                    help="基于图片宽度750rpx的比例"
                                    min={1}
                                    precision={0}
                                    style={{ width: '100%' }}
                                />
                                <Form.InputNumber
                                    field="h"
                                    label="高度"
                                    help="基于图片宽度750rpx的比例"
                                    min={1}
                                    precision={0}
                                    style={{ width: '100%' }}
                                />
                            </Form>
                            <Form.Label>链接</Form.Label>
                            <LinkSelector value={hotspot.l || ''} onChange={value => handleHotspotChange(index, 'l', value)} />
                            <Button type="danger" onClick={() => handleHotspotDelete(index)}>删除热区</Button>
                        </Form.Section>
                    );
                })}
            </Form.Section>

            <ModuleStyleForm
                style={data.style || defaultData.style}
                onChange={(newStyle) => {
                    console.log('修改模块样式:', newStyle);
                    onChange?.({ ...data, style: newStyle });
                }}
            />
        </Form>
    );
};